/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
import { css, html, TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import parseAspectRatio from '@carbon/ibmdotcom-utilities/es/utilities/parseAspectRatio/parseAspectRatio.js';
import C4DContentBlock from '../content-block/content-block';
import '../horizontal-rule/horizontal-rule';
import '../content-block/content-block-heading';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import styles from './logo-grid.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * Logo grid.
 *
 * @element c4d-logo-grid
 * @csspart content-wrapper - The wrapper. Usage: 'c4d-logo-grid::part(content-wrapper)'
 * @csspart content-body - The content body. Usage: 'c4d-logo-grid::part(content-body)'
 * @csspart content - The content. Usage 'c4d-logo-grid::part(content)'
 * @csspart footer-container - The footer container. Usage: 'c4d-logo-grid::part(footer-containe)'
 * @csspart footer - The footer. Usage: 'c4d-logo-grid::part(footer)'
 * @csspart hr - The horizontal rule. Usage: 'c4d-logo-grid::part(hr)'
 */
@customElement(`${c4dPrefix}-logo-grid`)
class C4DLogoGrid extends StableSelectorMixin(C4DContentBlock) {
  protected _renderInnerBody() {
    const { _hasContent: hasContent, _hasMedia: hasMedia, logoCount } = this;

    const rowClasses = {
      [`${prefix}--logo-grid__row`]: true,
      [`${prefix}--logo-grid__${logoCount}-columns`]: logoCount,
    };

    return html`
      <div
        ?hidden="${!hasContent && !hasMedia}"
        class="${prefix}--content-block__children ${prefix}--content-layout__body"
        part="content-body">
        <div class="${classMap(rowClasses)}" part="content">
          ${this._renderContent()}${this._renderMedia()}
        </div>
      </div>
    `;
  }

  /**
   * @returns The footer content.
   */
  protected _renderFooter(): TemplateResult | string | void {
    const { _hasFooter: hasFooter, _handleSlotChange: handleSlotChange } = this;
    return html`
      <div
        ?hidden="${!hasFooter}"
        class="${prefix}--content-block__cta-row"
        part="footer-container">
        <div
          class="${prefix}--content-block__cta ${prefix}-content-block__cta-col"
          part="footer">
          <slot name="footer" @slotchange="${handleSlotChange}"></slot>
        </div>
      </div>
    `;
  }

  @property({ attribute: 'hide-border', reflect: true, type: Boolean })
  hideBorder = false;

  /**
   * Integer value that determines the number of columns in the grid.
   *
   * While any non-zero integer will work, styles will constrain values to between
   * two and four.
   */
  @property({ attribute: 'logo-count', reflect: true })
  logoCount?;

  /**
   * Aspect ratio of grid cells.
   *
   * Values should match the format `<integer><divider-character><integer>`.
   * Any non-digit character can work as a divider.
   */
  @property({ attribute: 'logo-ratio', reflect: true })
  logoRatio?;

  updated(changedProperties) {
    const { logoRatio } = this;
    if (changedProperties.has('logoRatio')) {
      if (logoRatio) {
        const ratioSplit = parseAspectRatio(logoRatio);
        if (ratioSplit.length === 2) {
          const [w, h] = ratioSplit;
          this.style.setProperty('--logo-ratio', `${w}/${h}`);
        }
      } else {
        this.style.removeProperty('--logo-ratio');
      }
    }
  }

  render() {
    return html`
      <div class="${prefix}--content-layout--logo-grid" part="content-wrapper">
        <slot name="heading"></slot>
        ${this._renderBody()}
      </div>
      ${!this.hideBorder ? html` <c4d-hr part="hr"></c4d-hr> ` : ``}
    `;
  }

  static get stableSelector() {
    return `${c4dPrefix}--logo-grid`;
  }

  // `styles` here is a `CSSResult` generated by custom WebPack loader
  static get styles() {
    return css`
      ${super.styles}${styles}
    `;
  }
}

console.warn(
  'The logo-grid component has been deprecated in favor of the content-section/block and image-group component. ' + //TODO confirm new image-group or images in grid
    'See content-section/block and image-group documentation for more information.'
);

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DLogoGrid;
